import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* Public marketing page for the Shop, Cal & Meal browser extension.
   Anyone can land here (no auth gate). Signed-in users get the pairing-code
   widget inline; logged-out users see a "Try free" CTA pointing to /login. */

const STORES = [
  { id: "tesco", name: "Tesco", color: "#005EB8" },
  { id: "sainsburys", name: "Sainsbury's", color: "#F06C00" },
  { id: "asda", name: "ASDA", color: "#7DC242" },
  { id: "morrisons", name: "Morrisons", color: "#FFCB05" },
  { id: "waitrose", name: "Waitrose", color: "#5DAA80" },
  { id: "aldi", name: "Aldi", color: "#00558C" },
];

const DEMO_ITEMS = [
  { name: "Wholemeal pasta", qty: "500g" },
  { name: "Lean beef mince", qty: "500g" },
  { name: "Chopped tomatoes", qty: "2 tins" },
  { name: "Red onion", qty: "2" },
  { name: "Garlic", qty: "4 cloves" },
  { name: "Olive oil", qty: "1 tbsp" },
  { name: "Cheddar cheese", qty: "100g" },
];

export default function ExtensionLanding() {
  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#2A332C]">
      <TopBar />
      <Hero />
      <DemoStrip />
      <Steps />
      <SupermarketGrid />
      <MobileBlock />
      <PairingBlock />
      <FaqSection />
      <Footer />
    </div>
  );
}

/* ----------------------------------- chrome ----------------------------------- */
function TopBar() {
  const { user } = useAuth();
  return (
    <header className="px-5 md:px-10 py-5 flex items-center justify-between border-b border-[#E5E2DA] bg-[#FAF9F6]/80 backdrop-blur sticky top-0 z-30">
      <Link to="/" className="flex items-center gap-2 font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
        <span className="w-9 h-9 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-lg">🥗</span>
        <span className="text-lg">Shop, Cal & Meal</span>
      </Link>
      <div className="flex items-center gap-3">
        {user ? (
          <Link to="/app" className="text-sm font-medium text-[#3B5A3E] hover:underline" data-testid="topbar-app-link">
            Open app →
          </Link>
        ) : (
          <Link to="/login" className="ff-btn-primary text-sm" data-testid="topbar-login">
            Sign in
          </Link>
        )}
      </div>
    </header>
  );
}

/* ----------------------------------- hero ----------------------------------- */
function Hero() {
  const { user } = useAuth();
  return (
    <section className="px-5 md:px-10 pt-14 md:pt-24 pb-10 md:pb-16 max-w-6xl mx-auto">
      <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#3B5A3E]/10 text-[#3B5A3E] text-xs font-semibold uppercase tracking-[0.18em] px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#3B5A3E] animate-pulse" /> Free · Chrome · Firefox
          </span>
          <h1
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Save 20 minutes
            <br />
            per food shop.
          </h1>
          <p className="mt-5 text-lg text-[#5C6B61] max-w-xl leading-relaxed">
            Build your basket in Shop, Cal & Meal. Click the 🥗 icon. The extension <strong className="text-[#2A332C]">queues each item</strong> at Tesco, Sainsbury&apos;s, ASDA, Morrisons, Waitrose, and Aldi — one tap to add, top result every time. No typing, no missed items.
          </p>
          <div className="mt-7 flex items-center gap-4 flex-wrap">
            {user ? (
              <a href="#pair" className="ff-btn-primary text-base" data-testid="hero-pair-cta">
                Get your pairing code →
              </a>
            ) : (
              <Link to="/login" className="ff-btn-primary text-base" data-testid="hero-signup-cta">
                Start free →
              </Link>
            )}
            <a
              href="#how"
              className="text-sm font-medium text-[#3B5A3E] hover:underline underline-offset-4"
              data-testid="hero-how-link"
            >
              See it work ↓
            </a>
            <a
              href="#mobile"
              className="text-sm font-medium text-[#5C6B61] hover:text-[#3B5A3E] hover:underline underline-offset-4"
              data-testid="hero-mobile-link"
            >
              📱 On your phone?
            </a>
          </div>
          <p className="mt-4 text-xs text-[#8B9690]">No card needed · Works with your existing Tesco/Sainsbury&apos;s/ASDA/etc. login</p>
        </div>

        {/* live mini-extension preview */}
        <HeroAnimatedExtension />
      </div>
    </section>
  );
}

/* The reusable little browser-extension popup mock that runs the auto-fill
   animation on a loop. Designed to look identical to the real popup so users
   know exactly what they'll see. */
function HeroAnimatedExtension() {
  const [phase, setPhase] = useState(0); // 0=queued 1..N=adding 99=done
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let i = 0;
    const seq = [800, 900, 900, 900, 900, 900, 900, 900, 1400, 2000]; // ms per step
    const tickStep = () => {
      setPhase((p) => {
        if (p >= DEMO_ITEMS.length) return 0; // loop
        return p + 1;
      });
      setTick((t) => t + 1);
      i = (i + 1) % seq.length;
      timer = setTimeout(tickStep, seq[i]);
    };
    let timer = setTimeout(tickStep, 800);
    return () => clearTimeout(timer);
  }, []);

  const completed = Math.min(phase, DEMO_ITEMS.length);
  const isDone = phase >= DEMO_ITEMS.length;

  return (
    <div className="relative mx-auto md:mx-0 md:ml-auto" data-testid="hero-animated-extension">
      {/* faux browser frame */}
      <div className="absolute -top-6 -left-6 right-10 bottom-12 rounded-3xl bg-gradient-to-br from-[#3B5A3E]/10 to-[#C36952]/10 -z-10 blur-2xl" />
      <div className="w-[340px] sm:w-[380px] rounded-3xl bg-white shadow-[0_30px_60px_-20px_rgba(0,0,0,0.15)] border border-[#E5E2DA] overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#E5E2DA]">
          <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
          <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
          <span className="w-3 h-3 rounded-full bg-[#28C840]" />
          <span className="ml-3 text-xs text-[#8B9690] truncate">www.tesco.com/groceries</span>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
            <span className="w-7 h-7 rounded-full bg-[#3B5A3E] text-white inline-flex items-center justify-center text-sm">🥗</span>
            <span className="text-[15px]">Shop, Cal & Meal</span>
            <span className="ml-auto text-[10px] text-[#8B9690]">v2.0</span>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div>
              <div className="ff-label">Latest basket</div>
              <div className="font-medium text-[15px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                Weeknight bolognese
              </div>
            </div>
            <span className="text-xs text-[#3B5A3E] font-semibold">{isDone ? "Done" : `${completed}/${DEMO_ITEMS.length}`}</span>
          </div>

          <div className="mt-3 rounded-2xl border border-[#E5E2DA] bg-[#FAF9F6] divide-y divide-[#EAE6DD]">
            {DEMO_ITEMS.map((it, i) => {
              const status = i < completed - 1 ? "added" : i === completed - 1 ? "adding" : "queued";
              return <DemoRow key={it.name} item={it} status={status} />;
            })}
          </div>

          <div className="mt-4 h-2 rounded-full bg-[#F3F0EA] overflow-hidden">
            <div
              className="h-full bg-[#3B5A3E] transition-all duration-500"
              style={{ width: `${(completed / DEMO_ITEMS.length) * 100}%` }}
            />
          </div>
          <div className="mt-3 text-xs text-[#5C6B61]">
            {isDone ? "✓ Basket filled — head to checkout" : `Adding to Tesco… ${tick > 0 ? `tab ${completed}` : "starting"}`}
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoRow({ item, status }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 text-[13px]">
      <span className={`flex-1 truncate ${status === "added" ? "text-[#8B9690] line-through" : "text-[#2A332C]"}`}>
        {item.name}
      </span>
      <span className="text-[11px] text-[#8B9690]">{item.qty}</span>
      <span
        className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${
          status === "added"
            ? "bg-[#E4F1E5] text-[#2F7A3D]"
            : status === "adding"
            ? "bg-[#FFF6E3] text-[#B07F19] animate-pulse"
            : "bg-[#F3F0EA] text-[#8B9690]"
        }`}
      >
        {status === "added" ? "added" : status === "adding" ? "adding…" : "queued"}
      </span>
    </div>
  );
}

/* ----------------------------------- proof strip ----------------------------------- */
function DemoStrip() {
  return (
    <section className="px-5 md:px-10 py-8 border-y border-[#E5E2DA] bg-white">
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
        <Stat n="6" label="UK supermarkets" />
        <Stat n="1-tap" label="per item" />
        <Stat n="~20 min" label="saved per shop" />
        <Stat n="£0" label="forever free" />
      </div>
    </section>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="text-3xl md:text-4xl font-semibold text-[#3B5A3E]" style={{ fontFamily: "Outfit, sans-serif" }}>
        {n}
      </div>
      <div className="text-xs uppercase tracking-[0.18em] text-[#8B9690] mt-1">{label}</div>
    </div>
  );
}

/* ----------------------------------- steps ----------------------------------- */
function Steps() {
  return (
    <section id="how" className="px-5 md:px-10 py-16 md:py-24 max-w-6xl mx-auto">
      <div className="max-w-2xl">
        <div className="ff-label">How it works</div>
        <h2 className="text-3xl md:text-4xl font-semibold mt-2 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Three clicks. Trolley loaded.
        </h2>
      </div>
      <div className="mt-10 grid md:grid-cols-3 gap-6">
        <Step n="1" title="Plan it in Shop, Cal & Meal">
          Browse <em>Discover</em>, paste any recipe URL, or scan a photo of food. We&apos;ll merge ingredients into a single basket with quantities.
        </Step>
        <Step n="2" title="Click the 🥗 icon">
          Pin the extension. Pick a supermarket. Choose <strong>Auto-add</strong> (we do everything) or <strong>Assisted</strong> (we open tabs, you click Add).
        </Step>
        <Step n="3" title="Check out">
          The extension opens one hidden tab per item and clicks the top result&apos;s &ldquo;Add to basket&rdquo;. Head to your supermarket cart, check the substitutions, and check out.
        </Step>
      </div>
    </section>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="ff-card p-6 md:p-8">
      <div className="w-10 h-10 rounded-2xl bg-[#3B5A3E] text-white font-semibold flex items-center justify-center" style={{ fontFamily: "Outfit, sans-serif" }}>
        {n}
      </div>
      <h3 className="mt-4 text-lg font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h3>
      <p className="mt-2 text-sm text-[#5C6B61] leading-relaxed">{children}</p>
    </div>
  );
}

/* ----------------------------------- stores ----------------------------------- */
function SupermarketGrid() {
  return (
    <section className="px-5 md:px-10 py-10 md:py-16 bg-white border-y border-[#E5E2DA]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center">
          <div className="ff-label">Works with</div>
          <h2 className="text-2xl md:text-3xl font-semibold mt-2 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            The UK&apos;s six biggest grocers
          </h2>
        </div>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
          {STORES.map((s) => (
            <div
              key={s.id}
              className="rounded-2xl py-4 md:py-6 text-center font-semibold border border-[#E5E2DA] hover:scale-[1.02] transition-transform"
              style={{ background: `${s.color}10`, color: s.color, fontFamily: "Outfit, sans-serif" }}
              data-testid={`store-${s.id}`}
            >
              {s.name}
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-[#8B9690] max-w-xl mx-auto">
          You must be logged into the supermarket in the same browser with a delivery postcode set. The extension presses the Add button you&apos;d press anyway.
        </p>
      </div>
    </section>
  );
}

/* ----------------------------------- mobile ----------------------------------- */
function MobileBlock() {
  const { user } = useAuth();
  return (
    <section id="mobile" className="px-5 md:px-10 py-16 md:py-24 max-w-6xl mx-auto">
      <div className="max-w-2xl">
        <div className="ff-label">On your phone?</div>
        <h2 className="text-3xl md:text-4xl font-semibold mt-2 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          One tap. Opens straight in your supermarket app.
        </h2>
        <p className="mt-3 text-[#5C6B61] leading-relaxed">
          Apple & Google block sideloaded extensions on mobile, so we built the same auto-add flow <strong>directly into Shop, Cal & Meal</strong>. No installs, no zips, no dev mode — just tap.
        </p>
      </div>

      {/* PRIMARY: in-app Shop-with-app — works on every phone */}
      <div
        className="mt-8 ff-card p-6 md:p-10 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #3B5A3E 0%, #2A402D 100%)" }}
      >
        <span className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/5" />
        <span className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-[#C36952]/15" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-2xl bg-white text-[#3B5A3E] flex items-center justify-center text-2xl">📱</span>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/70 font-semibold">Recommended for phone</div>
              <h3 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
                &ldquo;Shop with app&rdquo; mode
              </h3>
            </div>
          </div>
          <p className="mt-4 text-white/85 leading-relaxed max-w-2xl">
            Already inside Shop, Cal & Meal. Pick a supermarket and we&apos;ll open every item in the <strong>Tesco / Sainsbury&apos;s / ASDA / Morrisons / Waitrose / Aldi app</strong> one at a time. Don&apos;t have the app? The button installs it from your phone&apos;s store automatically.
          </p>
          <ol className="mt-5 space-y-2 text-sm text-white/95">
            <li className="flex gap-2.5"><PillLight>1</PillLight><span>Open Shop, Cal & Meal → <strong>Shopping</strong> → tap your basket</span></li>
            <li className="flex gap-2.5"><PillLight>2</PillLight><span>Pick a supermarket → tap <strong>📱 Shop with app</strong></span></li>
            <li className="flex gap-2.5"><PillLight>3</PillLight><span>Item pops in their app. Tap Add. Tap <strong>Next item</strong> in Shop, Cal & Meal.</span></li>
            <li className="flex gap-2.5"><PillLight>4</PillLight><span>Ticked items sync back so nothing&apos;s missed</span></li>
          </ol>
          <Link
            to={user ? "/app/shopping" : "/login"}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-[#3B5A3E] font-medium text-sm px-5 py-2.5 hover:bg-[#F3F0EA] transition"
            data-testid="mobile-shop-cta"
          >
            {user ? "Go to my shopping lists →" : "Sign up to try it free →"}
          </Link>
          <p className="mt-3 text-xs text-white/60">Works on iPhone, Android, and any phone with a browser. No install.</p>
        </div>
      </div>

      {/* SECONDARY: power-user collapsible extension path */}
      <details className="mt-6 ff-card p-5 md:p-6">
        <summary className="cursor-pointer flex items-center gap-3 list-none">
          <span className="w-10 h-10 rounded-2xl bg-[#3B5A3E]/10 text-[#3B5A3E] flex items-center justify-center text-lg">🤖</span>
          <div className="flex-1">
            <div className="ff-label">Power user · Android</div>
            <div className="text-sm font-medium text-[#2A332C]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Real browser extension (Edge Canary)
            </div>
          </div>
          <span className="text-[#8B9690] text-sm">▾</span>
        </summary>
        <div className="mt-4 pt-4 border-t border-[#EAE6DD]">
          <p className="text-sm text-[#5C6B61] leading-relaxed">
            If you&apos;d rather have the full browser-extension experience on Android (auto-add to web baskets in the background, multi-store fallback, AI substitutes), <strong>Microsoft Edge Canary</strong> supports our zip directly.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <a
              href="https://play.google.com/store/apps/details?id=com.microsoft.emmx.canary"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 ff-btn-primary text-sm"
              data-testid="edge-canary-play-store-btn"
            >
              Get Edge Canary
            </a>
            <a
              href={`${(typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL) ? process.env.REACT_APP_BACKEND_URL.replace(/\/+$/, "") : ""}/api/extension/download`}
              className="flex items-center justify-center gap-2 rounded-full border border-[#3B5A3E] text-[#3B5A3E] hover:bg-[#3B5A3E] hover:text-white text-sm font-medium px-5 py-2.5 transition"
              data-testid="android-download-zip-btn"
              download
            >
              ⬇ Download .zip
            </a>
          </div>
          <ol className="mt-4 space-y-2 text-sm text-[#5C6B61]" data-testid="kiwi-steps">
            <li className="flex gap-2.5"><Pill>1</Pill><span>Install Edge Canary, reopen this page in Edge & tap <strong>Download .zip</strong></span></li>
            <li className="flex gap-2.5"><Pill>2</Pill><span>Settings → About → tap build number 7× to unlock Developer options</span></li>
            <li className="flex gap-2.5"><Pill>3</Pill><span>Developer options → <strong>Extension install by .crx/.zip</strong> → pick the zip</span></li>
            <li className="flex gap-2.5"><Pill>4</Pill><span>Tap the 🥗 icon in the ⋯ menu → paste your pairing code</span></li>
          </ol>
        </div>
      </details>

      <p className="mt-8 text-center text-xs text-[#8B9690] max-w-2xl mx-auto">
        Native iOS Safari Web Extension on the App Store coming later. Want it sooner? <a className="underline" href="mailto:hello@smartershop.xyz">Tell us</a>.
      </p>
    </section>
  );
}

function Pill({ children }) {
  return (
    <span className="flex-none w-6 h-6 rounded-full bg-[#3B5A3E] text-white text-xs font-semibold flex items-center justify-center">{children}</span>
  );
}
function PillLight({ children }) {
  return (
    <span className="flex-none w-6 h-6 rounded-full bg-white/20 text-white text-xs font-semibold flex items-center justify-center">{children}</span>
  );
}

/* ----------------------------------- pairing ----------------------------------- */
function PairingBlock() {
  const { user } = useAuth();
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  return (
    <section id="pair" className="px-5 md:px-10 py-16 md:py-24 max-w-6xl mx-auto">
      <div className="grid md:grid-cols-2 gap-10 items-start">
        <div>
          <div className="ff-label">Install</div>
          <h2 className="text-3xl md:text-4xl font-semibold mt-2 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Two minutes. One time.
          </h2>
          <p className="mt-4 text-[#5C6B61] leading-relaxed">
            Chrome Web Store & Firefox AMO listings land soon. Until then sideload — it&apos;s a 4-step copy-paste.
          </p>

          <a
            href={`${apiBase}/api/extension/download`}
            className="mt-6 inline-flex items-center gap-2 ff-btn-primary text-sm"
            data-testid="download-extension-btn"
            download
          >
            ⬇ Download extension (.zip · ~16 KB)
          </a>

          <ol className="mt-6 space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-none w-6 h-6 rounded-full bg-[#3B5A3E] text-white text-xs font-semibold flex items-center justify-center">1</span>
              <span><strong>Unzip</strong> the file — you&apos;ll get a <span className="font-mono text-[#2A332C]">shopcalmeal-extension</span> folder</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-none w-6 h-6 rounded-full bg-[#3B5A3E] text-white text-xs font-semibold flex items-center justify-center">2</span>
              <span>Open <span className="font-mono text-[#2A332C]">chrome://extensions</span> (Firefox: <span className="font-mono">about:debugging</span>) → toggle <strong>Developer mode</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-none w-6 h-6 rounded-full bg-[#3B5A3E] text-white text-xs font-semibold flex items-center justify-center">3</span>
              <span>Click <strong>Load unpacked</strong> → select the unzipped folder</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-none w-6 h-6 rounded-full bg-[#3B5A3E] text-white text-xs font-semibold flex items-center justify-center">4</span>
              <span>Pin the 🥗 icon → paste the pairing code from your Profile page</span>
            </li>
          </ol>
        </div>
        {user ? <InlinePairingCard /> : <CallToActionCard />}
      </div>
    </section>
  );
}

function CallToActionCard() {
  return (
    <div className="ff-card p-6 md:p-8 bg-gradient-to-br from-[#3B5A3E] to-[#2A402D] text-white">
      <h3 className="text-xl md:text-2xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
        Free Shop, Cal & Meal account
      </h3>
      <p className="mt-2 text-white/85 text-sm leading-relaxed">
        Sign up in 30 seconds. The extension needs a (free) Shop, Cal & Meal account to know which basket to fill.
      </p>
      <Link
        to="/login"
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-[#3B5A3E] font-medium text-sm px-5 py-2.5 hover:bg-[#F3F0EA] transition"
        data-testid="pair-block-signup"
      >
        Create my account →
      </Link>
      <div className="mt-6 pt-6 border-t border-white/20 text-xs text-white/70">
        Already have an account? <Link to="/login" className="underline">Sign in</Link>.
      </div>
    </div>
  );
}

function InlinePairingCard() {
  const [code, setCode] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000));
      setRemaining(left);
      if (left === 0) { setCode(null); setExpiresAt(null); }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/extension/pair/start");
      setCode(data.code);
      setExpiresAt(data.expires_at);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate code");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!code) return;
    try { await navigator.clipboard.writeText(code); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  const mm = String(Math.floor(remaining / 60));
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div className="ff-card p-6 md:p-8" data-testid="landing-pair-card">
      <div className="ff-label">Your pairing code</div>
      <h3 className="mt-2 text-xl md:text-2xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
        Generate, paste, go.
      </h3>
      <p className="mt-2 text-sm text-[#5C6B61] leading-relaxed">
        Single-use, 15-minute window. No password ever leaves this tab.
      </p>
      {!code ? (
        <button
          onClick={generate}
          disabled={busy}
          className="ff-btn-primary text-sm mt-5 disabled:opacity-60"
          data-testid="landing-generate-code-btn"
        >
          {busy ? "Generating…" : "Generate code"}
        </button>
      ) : (
        <div className="mt-5 space-y-3">
          <button
            onClick={copy}
            className="block w-full font-mono text-3xl md:text-4xl tracking-[0.4em] bg-[#F3F0EA] hover:bg-[#EAE6DD] rounded-2xl px-5 py-4 border border-[#E5E2DA] transition"
            data-testid="landing-code-display"
          >
            {code}
          </button>
          <div className="flex items-center justify-between text-xs text-[#5C6B61]">
            <span>Expires in <strong className="text-[#2A332C]">{mm}:{ss}</strong></span>
            <button onClick={generate} disabled={busy} className="text-[#3B5A3E] hover:underline">Regenerate</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------- faq ----------------------------------- */
const FAQS = [
  {
    q: "Is Shop, Cal & Meal logging into Tesco / Sainsbury's for me?",
    a: "No. You stay logged in. The extension simply clicks the Add button on the product page you'd open anyway — exactly what you do manually, just faster.",
  },
  {
    q: "What if the auto-add picks the wrong product?",
    a: "Switch to Assisted mode — every item opens in its own tab so you can pick the brand, size, or substitution before clicking Add. You stay in control.",
  },
  {
    q: "Will it work on my account / app?",
    a: "Chrome, Edge, Brave (Manifest V3) and Firefox 115+. Mobile browsers don't support extensions yet — use the Shop, Cal & Meal mobile app for that.",
  },
  {
    q: "What does the extension see?",
    a: "Just your basket (sent over HTTPS) and the supermarket pages the auto-add visits. No keystroke logging, no analytics, no third parties.",
  },
];

function FaqSection() {
  return (
    <section className="px-5 md:px-10 py-16 md:py-24 bg-white border-y border-[#E5E2DA]">
      <div className="max-w-3xl mx-auto">
        <div className="ff-label">FAQ</div>
        <h2 className="text-3xl md:text-4xl font-semibold mt-2 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Quick answers
        </h2>
        <div className="mt-8 space-y-3">
          {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} testid={`faq-${i}`} />)}
        </div>
      </div>
    </section>
  );
}

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ff-card overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left px-5 md:px-6 py-4 hover:bg-[#FAF9F6] transition"
        data-testid={testid}
      >
        <span className="font-medium text-[15px]" style={{ fontFamily: "Outfit, sans-serif" }}>{q}</span>
        <span className={`text-[#8B9690] transition-transform ${open ? "rotate-45" : ""}`}>＋</span>
      </button>
      {open && <p className="px-5 md:px-6 pb-5 text-sm text-[#5C6B61] leading-relaxed">{a}</p>}
    </div>
  );
}

/* ----------------------------------- footer ----------------------------------- */
function Footer() {
  return (
    <footer className="px-5 md:px-10 py-10 text-center text-xs text-[#8B9690]">
      Made with 🥗 by Shop, Cal & Meal · <Link to="/login" className="hover:text-[#3B5A3E]">Sign in</Link>
    </footer>
  );
}
